<template>
  <Section>
    <template #header>
      <div class="flex items-center gap-2 text-dark-neutral-500">
        <IdentificationIcon class="w-6 h-6" />
        <h2 class="font-semibold">Identifiers</h2>
      </div>
    </template>

    <div>
      <div class="px-6 py-4 border-b border-mid-neutral-50">
        <TextInput
          label="Name"
          v-model="identifiers.name"
          type="text"
          placeholder="Enter name"
          :is-required="true"
          :info-tooltip-text="'Provide the full legal name exactly as registered for compliance purposes.'"
        />
      </div>

      <div class="px-6 py-4 border-b border-mid-neutral-50">
        <TextInput
          v-model="identifiers.shortName"
          type="text"
          label="Short Name"
          placeholder="Enter short name"
          :info-tooltip-text="'Provide short name'"
        />
      </div>

      <div class="space-y-2 px-6 py-4 border-b border-mid-neutral-50">
        <div class="flex gap-0.5">
          <p
            class="block text-sm font-semibold leading-5 text-dark-neutral-500"
          >
            Common name misspellings
          </p>
          <InformationCircleIcon
            class="w-5 h-5 fill-neutral-400 text-white"
            v-tooltip="
              'Provide frequent misspellings or alternate spellings to support accurate matching.'
            "
          />
        </div>
        <div class="flex flex-wrap gap-4 items-center">
          <div
            v-for="misspelling in identifiers.misspellings"
            :key="'misspelling-' + misspelling"
            class="flex items-center gap-1 px-4 py-2.5 text-sm font-medium rounded-full text-primary-600 bg-primary-50"
          >
            <span
              v-tooltip="misspelling.length > 35 ? misspelling : ''"
              class="truncate max-w-[252px]"
              >{{ misspelling }}</span
            >
            <button @click="deleteMisspelling(misspelling)">
              <XMarkIcon class="w-4 h-4" />
            </button>
          </div>
          <FloatingTextInput
            @save="addMisspelling($event)"
            placeholder="Type misspelling"
          >
            <template #activator="{ show }">
              <button
                key=""
                class="flex items-center gap-1 text-sm border-2 p-2 pr-3 rounded-full text-slate-500 border-slate-300 border-dashed font-semibold"
                @click.stop="show"
              >
                <PlusIcon class="w-4 h-4" /> Add misspellings
              </button>
            </template>
          </FloatingTextInput>
        </div>
      </div>

      <div class="space-y-3 px-6 py-4 border-b border-mid-neutral-50">
        <div class="flex gap-0.5">
          <p
            class="block text-sm font-semibold leading-5 text-dark-neutral-500"
          >
            Common transliterations
          </p>

          <InformationCircleIcon
            class="w-5 h-5 fill-neutral-400 text-white"
            v-tooltip="
              'Provide common transliterations of the name from other languages or scripts.'
            "
          />
        </div>

        <div class="flex flex-wrap gap-4 items-center">
          <div
            v-for="transliteration in identifiers.transliterations"
            :key="'transliteration-' + transliteration"
            class="flex items-center gap-1 px-4 py-2.5 text-sm font-medium rounded-full text-primary-600 bg-primary-50"
          >
            <span
              v-tooltip="transliteration.length > 35 ? transliteration : ''"
              class="truncate max-w-[252px]"
              >{{ transliteration }}</span
            >
            <button @click="deleteTransliteration(transliteration)">
              <XMarkIcon class="w-4 h-4" />
            </button>
          </div>
          <FloatingTextInput
            @save="addTransliteration($event)"
            placeholder="Type transliteration"
          >
            <template #activator="{ show }">
              <button
                key=""
                class="flex items-center gap-1 text-sm border-2 p-2 pr-3 rounded-full text-slate-500 border-slate-300 border-dashed font-semibold"
                @click.stop="show"
              >
                <PlusIcon class="w-4 h-4" /> Add transliterations
              </button>
            </template>
          </FloatingTextInput>
        </div>
      </div>
    </div>
  </Section>
</template>

<script setup lang="ts">
import { useIDBKeyval } from "@vueuse/integrations/useIDBKeyval";

import {
  IdentificationIcon,
  InformationCircleIcon,
  PlusIcon,
  XMarkIcon,
} from "@heroicons/vue/24/outline";
import { FloatingTextInput, TextInput } from "ui-components";
import Section from "components/Section.vue";

type Identifiers = {
  name: string;
  shortName: string;
  misspellings: string[];
  transliterations: string[];
};

const { data: identifiers } = useIDBKeyval<Identifiers>(
  "identifiers",
  {
    name: "",
    shortName: "",
    misspellings: [],
    transliterations: [],
  },
  { shallow: false }
);

function addMisspelling(misspelling: string) {
  const trimmed = misspelling.trim();
  if (trimmed && !identifiers.value.misspellings.includes(trimmed)) {
    identifiers.value.misspellings.push(trimmed);
  }
}

function deleteMisspelling(toDelete: string) {
  identifiers.value.misspellings = identifiers.value.misspellings.filter(
    (m) => m !== toDelete
  );
}

function addTransliteration(transliteration: string) {
  const trimmed = transliteration.trim();
  if (trimmed && !identifiers.value.transliterations.includes(trimmed)) {
    identifiers.value.transliterations.push(trimmed);
  }
}

function deleteTransliteration(transliteration: string) {
  identifiers.value.transliterations =
    identifiers.value.transliterations.filter((m) => m !== transliteration);
}
</script>
